import { useState, type FC } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "@privacy/translations";
import {
  addCustomerLibraryProcessingToOrganisationMutationOptions,
  responsibleCandidatesForTenantsQueryOptions,
  type CustomerLibraryProcessing,
} from "../api/registrations";
import { hasErrorCode } from "../api/errors";
import type { ConnectedTenant } from "../api/registrations.types";
import * as FormDialog from "../shared/FormDialog";
import { ResponsibleField, useResponsiblePick } from "../shared/ResponsibleField";
import { CreatableTenantsSelect, useCreatableTenants } from "../pages/RegistrationsGridPage/CreatableTenantsSelect";

export const AddFromLibraryDialog: FC<{ processing: CustomerLibraryProcessing; onClose: () => void }> = ({
  processing,
  onClose,
}) => {
  const { t } = useTranslation();
  const addMutation = useMutation(addCustomerLibraryProcessingToOrganisationMutationOptions());
  const { tenantHierarchy, creatableTenants, takesOneUnit } = useCreatableTenants();

  const [pickedTenants, setPickedTenants] = useState<Array<ConnectedTenant> | null>(null);
  const defaultTenant =
    creatableTenants.find(tenant => tenant.id === tenantHierarchy?.id) ??
    (creatableTenants.length === 1 ? creatableTenants[0] : undefined);
  const selectedTenants = pickedTenants ?? (defaultTenant ? [defaultTenant] : []);
  const emptiedByUser = pickedTenants?.length === 0;

  const selectedTenantIds = selectedTenants.map(tenant => tenant.id);
  const responsibleCandidatesQuery = useQuery({
    ...responsibleCandidatesForTenantsQueryOptions(selectedTenantIds),
    enabled: selectedTenantIds.length > 0,
  });
  const responsiblePick = useResponsiblePick(selectedTenantIds, responsibleCandidatesQuery, { useDefault: true });
  const responsibleId = responsiblePick.responsibleId;
  const responsible = responsiblePick.candidates?.find(candidate => candidate.userId === responsibleId);

  const resetError = () => {
    if (addMutation.isError) addMutation.reset();
  };

  return (
    <FormDialog.Dialog isOpen={true}>
      <FormDialog.Title>
        {t("SharedProcessing.OrganisationLibrary.AddDialog.Title", { name: processing.name })}
      </FormDialog.Title>

      <FormDialog.Form
        onSubmit={event => {
          event.preventDefault();
          if (!responsibleId) return;
          addMutation.mutate(
            { id: processing.id, tenantIds: selectedTenantIds, responsibleId },
            { onSuccess: onClose }
          );
        }}
      >
        <FormDialog.Content>
          <FormDialog.FormError
            isError={addMutation.isError}
            errorMessage={
              hasErrorCode(addMutation.error, "RESPONSIBLE_CANNOT_WORK_ON_UNITS")
                ? t("SharedProcessing.OrganisationLibrary.AddDialog.ResponsibleCannotWorkOnUnits", {
                    name: responsible ? `${responsible.firstName} ${responsible.lastName}` : "",
                  })
                : hasErrorCode(addMutation.error, "DESCRIPTION_NOT_APPROVED")
                  ? t("SharedProcessing.OrganisationLibrary.AddDialog.DescriptionNotApproved", {
                      name: processing.name,
                    })
                  : undefined
            }
          />
          <FormDialog.Fields>
            <FormDialog.Field
              errorMessage={
                emptiedByUser
                  ? t(
                      takesOneUnit
                        ? "CreateRegistration.Dialog.TenantError.One"
                        : "CreateRegistration.Dialog.TenantError.Several"
                    )
                  : undefined
              }
            >
              <FormDialog.Label htmlFor="addFromLibraryTenants">
                {t(
                  takesOneUnit
                    ? "CreateRegistration.Dialog.TenantsLabel.One"
                    : "CreateRegistration.Dialog.TenantsLabel.Several"
                )}
              </FormDialog.Label>
              <CreatableTenantsSelect
                id="addFromLibraryTenants"
                selectedTenants={selectedTenants}
                onChange={tenants => {
                  setPickedTenants(tenants);
                  resetError();
                }}
                error={emptiedByUser}
              />
            </FormDialog.Field>
            <ResponsibleField
              id="addFromLibraryResponsible"
              tenantCount={selectedTenantIds.length}
              candidates={responsiblePick.candidates}
              responsibleId={responsibleId}
              onSelect={userId => {
                responsiblePick.pick(userId);
                resetError();
              }}
            />
          </FormDialog.Fields>
        </FormDialog.Content>

        <FormDialog.ActionButtons>
          <FormDialog.CancelButton onClick={onClose} />
          <FormDialog.SubmitButton
            isSubmitting={addMutation.isPending}
            isDisabled={selectedTenants.length === 0 || !responsibleId}
          >
            {t("SharedProcessing.PresentIn.Add")}
          </FormDialog.SubmitButton>
        </FormDialog.ActionButtons>
      </FormDialog.Form>
    </FormDialog.Dialog>
  );
};
